// Layer 3 (Transformations) — the Process route (polish.P17 #704, locked comp
// `.impeccable/mocks/decision/process.html` A, with B's variable read on the
// loop row and C's full-width fallback). Pure projections over the seeds and
// engines: the loop table, the band's four facts, the header fact, the matched
// rule as a condition list, the control-room ask, the operator readings with
// their control charts. No React, no engine mutation.

import type { VizTone } from '@/versions/v1/modules/_viz/viz-tone';
import type { ControlLoopState } from '@/versions/v1/schema/advisory-issue';
import type { Case } from '@/versions/v1/schema/case';
import type { DiagnosticRule } from '@/versions/v1/schema/diagnostic-rule';
import type { Reading } from '@/versions/v1/schema/reading';
import type { RoleTable } from '@/versions/v1/schema/plant-role';
import type { SessionState } from '@/versions/v1/schema/scenario';
import type { ExcursionEvent } from '@/versions/v1/schema/spc';
import { allRules } from '@/versions/v1/shared/diagnostic-rule.transform';
import {
  CONTROL_LOOPS,
  EXTRA_ROLE_TABLES,
  PLANT_ROLES,
} from '@/versions/v1/shared/bunker-hill-seed';
import { TAG_MAPPING } from '@/versions/v1/shared/bunker-hill-spc';
import {
  MONITORED_VARIABLES,
  controlChart,
  formatSpcValue,
  type ControlChartViewModel,
} from '@/versions/v1/shared/SpcEngine';
import {
  formatBand,
  formatValue,
  readingByIdLookup,
} from '@/versions/v1/shared/reading.derive';
import { seriesAt, valueAt, SERIES_BY_READING_ID } from '@/versions/v1/shared/series.derive';
import { HOURLY_FEED_DIVISOR, HOURLY_FEED_READING_IDS } from '@/versions/v1/shared/bunker-hill-readings';
import { type SeriesCadence } from '@/versions/v1/schema/reading-series';
import { formatCaseClock, shiftOf } from '@/versions/v1/shared/clock.derive';

export type ProcessTab = 'loop-status' | 'readings';

export type ProcessCopy = {
  title: string;
};

export type LoopKpis = {
  manualCount: number;
  manualPct: number;
  total: number;
};

export type LoopEvidenceItem = {
  evidenceId: string;
  caseId: string;
  caseCode: string;
  caseTitle: string;
  text: string;
  loopTag: string;
  /** Who the ask is of, as the plant names the seat (`Control room`). */
  targetLabel: string;
  status: 'open' | 'answered';
  answer?: string;
  answeredAt?: string;
};

/** Tone of a mode pill, a band cell or a status chip: state tokens only. */
export type ProcessTone = 'warn' | 'info' | 'ok' | 'bad' | 'neutral';

export const PROCESS_TABS: { id: ProcessTab; label: string }[] = [
  { id: 'loop-status', label: 'Loop status' },
  { id: 'readings', label: 'Readings' },
];

export const LOOP_MODE_LABEL: Record<ControlLoopState['mode'], string> = {
  auto: 'Auto',
  manual: 'Manual',
  local: 'Local',
};

/** A loop held in Manual longer than this is the exception (A §M3 §5). */
export const MANUAL_DWELL_RULE_H = 4;

export const RECORD_READING_TITLE =
  'Record reading — manual entry not available yet.';

export function processCopy(): ProcessCopy {
  return { title: 'Process' };
}

export function controlLoops(): readonly ControlLoopState[] {
  return CONTROL_LOOPS;
}

/** Parse dwell strings like "2.4 h" for mode-tone rules; em dash returns null. */
export function parseDwellHours(duration: string): number | null {
  const match = duration.match(/^([\d.]+)\s*h/i);
  if (!match) return null;
  const value = Number.parseFloat(match[1] ?? '');
  return Number.isFinite(value) ? value : null;
}

/** Manual past the 4 h rule is the warning; Local is a maintenance hold
 *  (informational, adjudication 4); Auto is quiet. */
export function loopModeTone(
  mode: ControlLoopState['mode'],
  duration: string,
): ProcessTone {
  if (mode === 'local') return 'info';
  if (mode === 'manual') {
    const dwell = parseDwellHours(duration);
    if (dwell !== null && dwell > MANUAL_DWELL_RULE_H) return 'warn';
  }
  return 'neutral';
}

export function loopModePillLabel(loop: ControlLoopState): string {
  if (loop.duration === '—') return LOOP_MODE_LABEL[loop.mode];
  return `${LOOP_MODE_LABEL[loop.mode]} · ${loop.duration}`;
}

export function loopKpis(loops: readonly ControlLoopState[]): LoopKpis {
  const total = loops.length;
  const manualCount = loops.filter((loop) => loop.mode === 'manual').length;
  const manualPct = total === 0 ? 0 : Math.round((manualCount / total) * 100);
  return { manualCount, manualPct, total };
}

export function loopManualMeterTone(manualPct: number): VizTone {
  if (manualPct === 0) return 'ok';
  if (manualPct <= 25) return 'warn';
  return 'bad';
}

/** Rule whose signal pattern references the loop tag (e.g. dr-dart-valve ↔ LIC4010). */
export function ruleForLoopTag(tag: string): DiagnosticRule | undefined {
  const rules = allRules();
  return rules.find(
    (rule) =>
      rule.signalPattern.includes(tag) ||
      (tag === 'LIC4010' && rule.id === 'dr-dart-valve'),
  );
}

export function defaultSelectedLoopId(loops: readonly ControlLoopState[]): string {
  return loops.find((loop) => loop.tag === 'LIC4010')?.id ?? loops[0]?.id ?? '';
}

export function operatorReadingsTable(): RoleTable | undefined {
  return EXTRA_ROLE_TABLES.find(
    (table) =>
      table.roleId === 'operator' &&
      table.columns.includes('Normal range'),
  );
}

const OPERATOR_READING_KPV_MAP: Record<string, string> = {
  'rd-1': 'rd-kpv-bm-feed',
  'rd-2': 'rd-kpv-mill-dens',
  'rd-3': 'rd-kpv-regrind-p80',
  'rd-4': 'rd-kpv-pb-ph',
  'rd-5': 'rd-kpv-zn-ph',
};

/** Operator / KPV subline: value · band — reproduces dev spacing per row kind. */
export function operatorReadingSubline(
  rowId: string,
  fallback: readonly string[],
  readings?: readonly Reading[],
): string {
  const readingId = OPERATOR_READING_KPV_MAP[rowId];
  const reading = readingId
    ? (readings?.find((r) => r.id === readingId) ?? readingByIdLookup(readingId))
    : undefined;
  if (!reading) return `${fallback[1]} · ${fallback[3]}`;
  const withUnit = reading.unit !== 'pH';
  const bandUnit = reading.unit === 'pH' ? '' : reading.unit;
  const band = reading.band
    ? formatBand(reading.band, bandUnit, {
        withUnit: withUnit && bandUnit !== '',
        spacedDash: false,
      })
    : fallback[3];
  return `${formatValue(reading, { withUnit, decimals: reading.decimals })} · ${band}`;
}

/** Compact viewport: out-of-range readings first. */
export function operatorReadingsRowsForCompact(
  table: RoleTable,
): RoleTable['rows'][number][] {
  return [...table.rows].sort((a, b) => {
    const warnA = a.cells[2] === 'WARN' || a.cells[2] === 'HIGH' ? 0 : 1;
    const warnB = b.cells[2] === 'WARN' || b.cells[2] === 'HIGH' ? 0 : 1;
    return warnA - warnB;
  });
}

export function loopTags(loops: readonly ControlLoopState[]): readonly string[] {
  return loops.map((loop) => loop.tag);
}

function loopTagInText(text: string, tags: readonly string[]): string | undefined {
  return tags.find((tag) => text.includes(tag));
}

function roleLabel(roleId: string): string {
  return PLANT_ROLES.find((role) => role.id === roleId)?.label ?? roleId;
}

/** Open or session-answered loop-state asks whose tag appears on CONTROL_LOOPS. */
export function loopEvidenceItems(
  cases: readonly Case[],
  tags: readonly string[],
): LoopEvidenceItem[] {
  const items: LoopEvidenceItem[] = [];
  for (const caseItem of cases) {
    const code = caseItem.code;
    if (!code) continue;
    for (const request of caseItem.evidenceRequests) {
      if (request.kind !== 'loop-state') continue;
      const loopTag = loopTagInText(request.text, tags);
      if (!loopTag) continue;
      items.push({
        evidenceId: request.id,
        caseId: caseItem.id,
        caseCode: code,
        caseTitle: caseItem.title,
        text: request.text,
        loopTag,
        targetLabel: roleLabel(request.target),
        status: request.status,
        answer: request.answer,
        answeredAt: request.answeredAt,
      });
    }
  }
  return items;
}

export function loopEvidenceAnsweredLabel(answer: string): string {
  return `Answered ${answer.toUpperCase()}`;
}

/** A loop-state ask is answered with the loop's mode. */
export const LOOP_STATE_QUESTION = 'AUTO or MANUAL?';

function cursorClock(cursor: string): string {
  return cursor.slice(11, 16);
}

/** When the answer landed: the session answers at the cursor (`now`). */
export function evidenceAnsweredAtLabel(answeredAt: string | undefined, cursor: string): string {
  if (!answeredAt || answeredAt === 'now') return cursorClock(cursor);
  if (/^\d{2}:\d{2}$/.test(answeredAt)) return answeredAt;
  return answeredAt.slice(11, 16) || cursorClock(cursor);
}

// --- Clock ---------------------------------------------------------------

function minutesOf(hhmm: string): number | null {
  const match = hhmm.match(/^(\d{2}):(\d{2})$/);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

type ShiftWindow = { kind: 'day' | 'night'; startMin: number; endMin: number; cursorMin: number };

function shiftWindow(cursor: string): ShiftWindow {
  const shift = shiftOf(cursor);
  const startMin = minutesOf(shift.start.slice(11, 16)) ?? 0;
  const endMin = minutesOf(shift.end.slice(11, 16)) ?? 0;
  const cursorMin = minutesOf(cursorClock(cursor)) ?? 0;
  return { kind: shift.kind, startMin, endMin, cursorMin };
}

/** Minutes since the shift started, for a clock time on the plant's 24 h clock. */
function sinceShiftStart(minute: number, window: ShiftWindow): number {
  return (minute - window.startMin + 24 * 60) % (24 * 60);
}

/** A mode change inside the running shift, up to the cursor. A change at the
 *  shift start itself is the handover log, not a change (adjudication 9). */
function changedThisShift(loop: ControlLoopState, window: ShiftWindow): boolean {
  const at = loop.modeChangedAt ? minutesOf(loop.modeChangedAt) : null;
  if (at === null) return false;
  const offset = sinceShiftStart(at, window);
  return offset > 0 && offset <= sinceShiftStart(window.cursorMin, window);
}

export type ModeChangesFact = {
  label: string;
  count: number;
  /** The latest change this shift: `LIC4013` · `to Local at 09:05` · `Maint`. */
  latest?: { tag: string; text: string; by?: string };
  /** With no change this shift: how many loops held their mode. */
  heldCount: number;
};

export function modeChangesThisShift(
  loops: readonly ControlLoopState[],
  cursor: string,
  shortRole: (roleId: NonNullable<ControlLoopState['changedBy']>) => string,
): ModeChangesFact {
  const window = shiftWindow(cursor);
  const changed = loops
    .filter((loop) => changedThisShift(loop, window))
    .sort((a, b) => (a.modeChangedAt ?? '').localeCompare(b.modeChangedAt ?? ''));
  const last = changed[changed.length - 1];
  return {
    label: `Mode changes · ${window.kind} shift`,
    count: changed.length,
    latest: last
      ? {
          tag: last.tag,
          text: ` to ${LOOP_MODE_LABEL[last.mode]} at ${last.modeChangedAt}`,
          by: last.changedBy ? shortRole(last.changedBy) : undefined,
        }
      : undefined,
    heldCount: loops.length,
  };
}

/** `since 03:20, carried from night shift` or `since 09:05, this shift`. */
function sincePhrase(loop: ControlLoopState, cursor: string): string {
  const at = loop.modeChangedAt;
  if (!at) return '';
  const window = shiftWindow(cursor);
  const previous = window.kind === 'day' ? 'night' : 'day';
  return changedThisShift(loop, window)
    ? ` since ${at}, this shift`
    : ` since ${at}, carried from ${previous} shift`;
}

// --- SPC reads -------------------------------------------------------------

/** One charted variable, read once from the SPC engine and shown wherever the
 *  route draws it (row strip, selected chart): the same `controlChart` object
 *  and caption Plant model shows (adjudication 8). */
export type ProcessChart = {
  variableId: string;
  readingId: string;
  /** Instrument tag as the reading names it (`AT-150`). */
  tag?: string;
  model: ControlChartViewModel & { excursionRange?: { startAt: string; endAt: string } };
  /** `below LCL 10.4` · `above UCL 8.6` · `cyclical` · `in control`. */
  stateLabel: string;
  /** The latest point is beyond a control limit (state-blocked). */
  beyond: boolean;
  /** Which limit it is beyond, in the chart's words (`below LCL`). */
  limitSide?: 'below LCL' | 'above UCL';
  /** `last 24 h · hourly` or `last 8 shifts`. */
  windowLabel: string;
  /** The run rules whose span is shaded (`cyclical · widening`), if any. */
  runRulesLabel?: string;
};

const RUN_RULE_LABEL: Record<ExcursionEvent['rule'], string> = {
  'beyond-limit': 'beyond limit',
  'six-point-trend': 'six-point trend',
  'widening-variability': 'widening',
  cyclical: 'cyclical',
};

function windowLabel(points: readonly { at: string }[]): string {
  if (points.length < 2) return `last ${points.length} points`;
  const first = Date.parse(`${points[0]!.at}Z`);
  const second = Date.parse(`${points[1]!.at}Z`);
  const stepH = Math.round((second - first) / 3_600_000);
  if (stepH === 1) return `last ${points.length} h · hourly`;
  if (stepH === 12) return `last ${points.length} shifts`;
  if (stepH === 24) return `last ${points.length} days`;
  return `last ${points.length} points`;
}

export function variableForReading(readingId: string): string | undefined {
  return MONITORED_VARIABLES.find((variable) => variable.readingId === readingId)?.id;
}

/**
 * A chart drawn in another reading's scale (#735). The seed gives a `[derived]`
 * feed reading (`rd-kpv-bm-feed` = `rd-kpi-ore-td ÷ 24`, D14) no series of its
 * own; the series behind it is its source's (`rs-ore-td`), one fixed multiple
 * away. The Process route draws that source's chart projected onto the reading's
 * own scale and unit (`HOURLY_FEED_DIVISOR`), never a second series.
 */
type ChartProjection = {
  /** The reading whose unit and decimals the projected values are formatted in. */
  readingId: string;
  /** Divide the source series' values by this to reach the reading's own scale. */
  divisor: number;
};

/** The engine's chart, ÷ `divisor` on every value, moving range, σ and limit —
 *  a linear projection, so every point, flag, run rule and state is the
 *  engine's (`mr`-derived limits scale with the moving range). The caption is
 *  rebuilt from the projected limits so it prints in the projected unit. */
function projectChart(
  chart: ControlChartViewModel,
  projection: ChartProjection,
): ControlChartViewModel {
  const { divisor } = projection;
  if (divisor === 1) return chart;
  const reading = readingByIdLookup(projection.readingId);
  const scale = (value: number) => value / divisor;
  const limits = chart.limits
    ? {
        ...chart.limits,
        mean: scale(chart.limits.mean),
        ucl: scale(chart.limits.ucl),
        lcl: scale(chart.limits.lcl),
        mrMean: scale(chart.limits.mrMean),
        mrUcl: scale(chart.limits.mrUcl),
        sigma: scale(chart.limits.sigma),
      }
    : undefined;
  const spec =
    chart.spec && (chart.spec.lo !== undefined || chart.spec.hi !== undefined)
      ? {
          lo: chart.spec.lo === undefined ? undefined : scale(chart.spec.lo),
          hi: chart.spec.hi === undefined ? undefined : scale(chart.spec.hi),
        }
      : chart.spec;
  const format = (value: number) => (reading ? formatSpcValue(reading, value) : String(value));
  return {
    ...chart,
    readingId: projection.readingId,
    readingLabel: reading?.label ?? chart.readingLabel,
    points: chart.points.map((point) => ({
      ...point,
      value: scale(point.value),
      ...(point.mr === undefined ? {} : { mr: scale(point.mr) }),
    })),
    limits,
    spec,
    ...(chart.caption && limits
      ? {
          caption: {
            ...chart.caption,
            mean: format(limits.mean),
            ucl: format(limits.ucl),
            lcl: format(limits.lcl),
            sigma: format(limits.sigma),
          },
        }
      : {}),
  };
}

/** The chart a reading's own series gives it. A reading the seed states
 *  directly draws its monitored variable; a `[derived]` feed reading draws its
 *  source series projected onto its own scale (#735) — never no chart at all. */
export function chartForReading(readingId: string, state: SessionState): ProcessChart | undefined {
  const own = variableForReading(readingId);
  if (own) return processChart(own, state);
  if (!HOURLY_FEED_READING_IDS.includes(readingId)) return undefined;
  const source = variableForReading(DAILY_ORE_READING_ID);
  return source
    ? processChart(source, state, { readingId, divisor: HOURLY_FEED_DIVISOR })
    : undefined;
}

export function processChart(
  variableId: string,
  state: SessionState,
  projection?: ChartProjection,
): ProcessChart | undefined {
  const engineChart = controlChart(variableId, state);
  if (engineChart.phase === 'learning' || !engineChart.limits || engineChart.points.length === 0) return undefined;
  const chart = projection ? projectChart(engineChart, projection) : engineChart;
  const reading = readingByIdLookup(chart.readingId);
  const last = chart.points[chart.points.length - 1]!;
  const { ucl, lcl } = chart.limits!;
  const format = (value: number) => (reading ? formatSpcValue(reading, value) : String(value));
  const below = last.value < lcl;
  const above = last.value > ucl;
  const runRules = chart.events.filter((event) => event.rule !== 'beyond-limit' && event.points.length > 0);
  const runStamps = runRules.flatMap((event) => event.points).sort();
  const firstPoint = chart.points[0]!.at;
  const excursionRange = runStamps.length > 0
    ? { startAt: runStamps[0]! < firstPoint ? firstPoint : runStamps[0]!, endAt: runStamps[runStamps.length - 1]! }
    : undefined;
  const runRulesLabel = runRules.length > 0
    ? [...new Set([...runRules].sort((a, b) => a.detectedAt.localeCompare(b.detectedAt)).map((event) => RUN_RULE_LABEL[event.rule]))].join(' · ')
    : undefined;
  const primary = chart.events[0];
  const stateLabel = below
    ? `below LCL ${format(lcl)}`
    : above
      ? `above UCL ${format(ucl)}`
      : primary
        ? RUN_RULE_LABEL[primary.rule]
        : 'in control';
  return {
    variableId,
    readingId: chart.readingId,
    tag: reading?.tag,
    model: excursionRange ? { ...chart, excursionRange } : chart,
    stateLabel,
    beyond: below || above,
    limitSide: below ? 'below LCL' : above ? 'above UCL' : undefined,
    windowLabel: windowLabel(chart.points),
    runRulesLabel,
  };
}

// --- Basis: what each figure is measured over --------------------------------
//
// polish.P17 r1 (orchestrator ruling, issue #704): the WIC1201A loop row and the
// Ball mill fresh feed reading are two bases of the same instrument, not one
// quantity stated twice — 75.0 stph is `rs-wit-021`'s live hourly point, 71.7
// stph is `rd-kpi-ore-td ÷ 24`, the fresh-feed day rate every other route
// prints. Each cell names its own basis; no value and no series changes.

/** The reading every hourly fresh-feed projection is a 1/24 of (D14). */
const DAILY_ORE_READING_ID = 'rd-kpi-ore-td';

/** The period word the glass uses for a series cadence. */
const CADENCE_PERIOD: Record<SeriesCadence, string> = {
  hourly: 'hour',
  shift: 'shift',
  daily: 'day',
};

/**
 * The basis a derived reading carries, named from its own source: the source
 * series' period word and the source reading's unit, both read off the engine.
 * `rd-kpv-bm-feed` (71.7 stph) is `rd-kpi-ore-td ÷ 24`, so it reads
 * `day rate (st/d)`. Never a typed string; a reading the seed states directly
 * gets none. Kept to the one line the Readings table has at 1470 — the longer
 * `(st/d ÷ 24)` wrapped the Parameter column.
 */
export function derivedRateBasis(readingId: string): string | undefined {
  if (!HOURLY_FEED_READING_IDS.includes(readingId)) return undefined;
  const source = readingByIdLookup(DAILY_ORE_READING_ID);
  const series = SERIES_BY_READING_ID[DAILY_ORE_READING_ID];
  if (!source || !series) return undefined;
  return `${CADENCE_PERIOD[series.cadence]} rate (${source.unit})`;
}

// --- Loop table --------------------------------------------------------------

export type LoopPv = {
  /** The reading's name as the operator table names it (`Zn cell pH`). */
  readingName: string;
  tag: string;
  value: string;
  unit: string;
  /** The hour of the series point the value is — `11:00` while the point is on
   *  the cursor's production day, `07 Jun 20:00` when it is older. */
  at: string;
  chart: ProcessChart;
};

export type LoopRow = {
  id: string;
  tag: string;
  /** The loop name without its tag parenthetical (adjudication 3). */
  name: string;
  /** `LIC4010 · LIT4010 / FMEA LT-140`. */
  tagLine: string;
  modeLabel: string;
  tone: ProcessTone;
  since?: string;
  changedBy?: string;
  note: string;
  pv?: LoopPv;
  /** An open control-room ask about this loop. */
  ask?: LoopEvidenceItem;
};

function splitLoopName(name: string): { name: string; xref?: string } {
  const match = name.match(/^(.*?)\s*\((.+)\)\s*$/);
  if (!match) return { name };
  return { name: match[1]!, xref: match[2]! };
}

function loopPv(loop: ControlLoopState, state: SessionState): LoopPv | undefined {
  const mapping = TAG_MAPPING.find((row) => row.loopTag === loop.tag);
  if (!mapping) return undefined;
  const variableId = variableForReading(mapping.readingId);
  const reading = readingByIdLookup(mapping.readingId);
  const series = SERIES_BY_READING_ID[mapping.readingId];
  if (!variableId || !reading || !series) return undefined;
  const chart = processChart(variableId, state);
  const value = valueAt(mapping.readingId, state);
  const point = seriesAt(series.id, state).latest;
  if (!chart || value === undefined || !point) return undefined;
  return {
    readingName: readingNameFor(mapping.readingId, operatorReadingsTable()) ?? reading.label,
    tag: mapping.instrumentTag ?? reading.tag,
    value: formatSpcValue(reading, value),
    unit: reading.unit,
    at: formatCaseClock(point.at, state.cursor),
    chart,
  };
}

export function loopRows(
  loops: readonly ControlLoopState[],
  state: SessionState,
  evidence: readonly LoopEvidenceItem[],
  shortRole: (roleId: NonNullable<ControlLoopState['changedBy']>) => string,
): LoopRow[] {
  return loops.map((loop) => {
    const { name, xref } = splitLoopName(loop.name);
    return {
      id: loop.id,
      tag: loop.tag,
      name,
      tagLine: [loop.tag, xref].filter(Boolean).join(' · '),
      modeLabel: loopModePillLabel(loop),
      tone: loopModeTone(loop.mode, loop.duration),
      since: loop.modeChangedAt,
      changedBy: loop.changedBy ? shortRole(loop.changedBy) : undefined,
      note: loop.note,
      pv: loopPv(loop, state),
      ask: evidence.find((item) => item.loopTag === loop.tag && item.status === 'open'),
    };
  });
}

// --- Exception: the matched rule as a condition list --------------------------

export type RuleCondition = {
  text: string;
  /** The clause this route reads: the loop's own mode. */
  owned: boolean;
  met?: boolean;
  status?: string;
};

export type LoopException = {
  rule?: {
    diagnosis: string;
    conditions: RuleCondition[];
    action: string;
    actionActor: string;
  };
  /** The control-mode case opened on this loop (`LIC4010`). */
  caseRef?: { caseId: string; code: string; title: string };
};

const MODE_CLAUSE = /\bin (AUTO|MANUAL|LOCAL)\b/i;

/** Split the rule's IF clauses at `+`; only the loop-mode clause is judged here
 *  (adjudication 2), against the loop's mode at the cursor. */
export function ruleConditions(rule: DiagnosticRule, loop: ControlLoopState): RuleCondition[] {
  return rule.signalPattern
    .split(/\s+\+\s+/)
    .map((text) => text.trim())
    .filter(Boolean)
    .map((text) => {
      const clause = text.match(MODE_CLAUSE);
      if (!clause || !text.includes(loop.tag)) return { text, owned: false };
      const met = clause[1]!.toLowerCase() === loop.mode;
      return {
        text,
        owned: true,
        met,
        status: `${met ? 'met' : 'not met'} · ${LOOP_MODE_LABEL[loop.mode]} ${loop.duration}`,
      };
    });
}

export function loopException(
  loop: ControlLoopState,
  cases: readonly Case[],
): LoopException {
  const rule = ruleForLoopTag(loop.tag);
  const controlCase = cases.find(
    (caseItem) => caseItem.kind === 'control-mode' && caseItem.controlModeCheck?.tag === loop.tag && caseItem.code,
  );
  return {
    rule: rule
      ? {
          diagnosis: rule.diagnosis,
          conditions: ruleConditions(rule, loop),
          action: rule.recommendedAction,
          actionActor: roleLabel(rule.responsible),
        }
      : undefined,
    caseRef: controlCase
      ? { caseId: controlCase.id, code: controlCase.code!, title: controlCase.title }
      : undefined,
  };
}

// --- Readings ---------------------------------------------------------------

export type ReadingRow = {
  id: string;
  parameter: string;
  reading: string;
  range: string;
  status: string;
  statusTone: ProcessTone;
  /** The KPV reading the row reads (`rd-kpv-zn-ph`); absent on a row with no reading behind it. */
  readingId?: string;
  /** The basis a derived reading carries (`day rate (st/d ÷ 24)`); absent on a
   *  reading the seed states directly. */
  basis?: string;
  tag?: string;
  chart?: ProcessChart;
};

function statusTone(status: string): ProcessTone {
  if (status === 'WARN' || status === 'HIGH' || status === 'LATE' || status === 'DUE') return 'warn';
  if (status === 'OK' || status === 'RUN' || status === 'Done' || status === 'REG') return 'ok';
  return 'neutral';
}

export function readingRows(table: RoleTable, state: SessionState): ReadingRow[] {
  const column = (name: string) => table.columns.indexOf(name);
  const [iParam, iReading, iStatus, iRange] = ['Parameter', 'Reading', 'Status', 'Normal range'].map(column);
  return table.rows.map((row) => {
    const readingId = OPERATOR_READING_KPV_MAP[row.id];
    const chart = readingId ? chartForReading(readingId, state) : undefined;
    const status = row.cells[iStatus!] ?? '';
    return {
      id: row.id,
      parameter: row.cells[iParam!] ?? '',
      reading: row.cells[iReading!] ?? '',
      range: row.cells[iRange!] ?? '',
      status,
      statusTone: statusTone(status),
      readingId,
      basis: readingId ? derivedRateBasis(readingId) : undefined,
      tag: readingId ? readingByIdLookup(readingId)?.tag : undefined,
      chart,
    };
  });
}

/** The out-of-range reading comes first to the eye: it is the default selection. */
export function defaultSelectedReadingId(rows: readonly ReadingRow[]): string {
  return rows.find((row) => row.statusTone === 'warn')?.id
    ?? rows.find((row) => row.chart)?.id
    ?? rows[0]?.id
    ?? '';
}

/** The operator's name for a reading (`Zn cell pH`), so the route names it once. */
export function readingNameFor(readingId: string, table: RoleTable | undefined): string | undefined {
  const rowId = Object.entries(OPERATOR_READING_KPV_MAP).find(([, id]) => id === readingId)?.[0];
  const row = rowId ? table?.rows.find((candidate) => candidate.id === rowId) : undefined;
  return row?.cells[table!.columns.indexOf('Parameter')];
}

export type HeldBy = {
  loopId: string;
  tag: string;
  name: string;
  modeLabel: string;
  tone: ProcessTone;
  ask?: LoopEvidenceItem;
};

/** The control loop that holds a charted reading (`AIC5002` holds AT-150). */
export function heldByLoop(
  readingId: string | undefined,
  loops: readonly ControlLoopState[],
  evidence: readonly LoopEvidenceItem[],
): HeldBy | undefined {
  if (!readingId) return undefined;
  const mapping = TAG_MAPPING.find((row) => row.readingId === readingId && row.gateLoopId && row.loopTag);
  const loop = mapping ? loops.find((candidate) => candidate.id === mapping.gateLoopId && candidate.tag === mapping.loopTag) : undefined;
  if (!loop) return undefined;
  return {
    loopId: loop.id,
    tag: loop.tag,
    name: splitLoopName(loop.name).name,
    modeLabel: loopModePillLabel(loop),
    tone: loopModeTone(loop.mode, loop.duration),
    ask: evidence.find((item) => item.loopTag === loop.tag && item.status === 'open'),
  };
}

export function readingIdForRow(rowId: string): string | undefined {
  return OPERATOR_READING_KPV_MAP[rowId];
}

export type CompactReadingRow = {
  id: string;
  parameter: string;
  subline: string;
  status: string;
  tone: ProcessTone;
};

/** The phone list: out-of-range first, each value · band read at the cursor. */
export function compactReadingRows(table: RoleTable, readings: readonly Reading[]): CompactReadingRow[] {
  return operatorReadingsRowsForCompact(table).map((row) => {
    const [parameter, , status] = row.cells;
    return {
      id: row.id,
      parameter: parameter ?? '',
      subline: operatorReadingSubline(row.id, row.cells, readings),
      status: status ?? '',
      tone: statusTone(status ?? ''),
    };
  });
}

// --- Header, band, tab headlines -------------------------------------------------

export type BandSegment = { text: string; strong?: boolean };

export type ProcessBandCell = {
  id: 'manual' | 'longest' | 'readings' | 'ask';
  name: string;
  figure: string;
  unit?: string;
  comparison: BandSegment[];
  tone: ProcessTone;
  meter?: { value: number; max: number; tone: VizTone };
  /** The control-room ask the cell names (case chip + who is asked). */
  ask?: { item: LoopEvidenceItem; open: boolean };
};

export function processBand(
  loops: readonly ControlLoopState[],
  rows: readonly ReadingRow[],
  evidence: readonly LoopEvidenceItem[],
  cursor: string,
): ProcessBandCell[] {
  const kpis = loopKpis(loops);
  const local = loops.filter((loop) => loop.mode === 'local');
  const localForMaintenance = local.length > 0 && local.every((loop) => loop.changedBy === 'maintenance');
  const manualComparison: BandSegment[] = [
    { text: `${kpis.manualPct}%`, strong: true },
    { text: ' of loops' },
  ];
  if (local.length > 0) {
    manualComparison.push({ text: ` · ${local.length} more in Local${localForMaintenance ? ' for maintenance' : ''}` });
  }

  const manual = loops
    .filter((loop) => loop.mode === 'manual')
    .map((loop) => ({ loop, hours: parseDwellHours(loop.duration) ?? 0 }))
    .sort((a, b) => b.hours - a.hours);
  const longest = manual[0];
  const rule = `${MANUAL_DWELL_RULE_H} h`;
  const longestCell: ProcessBandCell = longest
    ? {
        id: 'longest',
        name: 'Longest in manual',
        figure: longest.loop.duration,
        comparison: [
          { text: 'vs ' },
          { text: rule, strong: true },
          { text: ' rule · ' },
          { text: longest.loop.tag, strong: true },
          { text: sincePhrase(longest.loop, cursor) },
        ],
        tone: longest.hours > MANUAL_DWELL_RULE_H ? 'warn' : 'neutral',
      }
    : {
        id: 'longest',
        name: 'Longest in manual',
        figure: '0 h',
        comparison: [{ text: 'vs ' }, { text: rule, strong: true }, { text: ' rule' }],
        tone: 'neutral',
      };

  const inRange = rows.filter((row) => row.statusTone !== 'warn');
  const out = rows.find((row) => row.statusTone === 'warn');
  const readingsComparison: BandSegment[] = out
    ? [
        { text: `${out.parameter} ${out.reading}`, strong: true },
        { text: ` vs ${out.range}` },
        ...(out.chart?.limitSide ? [{ text: ` · ${out.chart.limitSide} on its chart` }] : []),
      ]
    : [{ text: `${rows.filter((row) => row.chart).length} charted`, strong: true }, { text: ' · in their normal range' }];

  const open = evidence.filter((item) => item.status === 'open');
  const answered = evidence.filter((item) => item.status === 'answered');
  const askItem = open[0] ?? answered[0];
  const askCell: ProcessBandCell = {
    id: 'ask',
    name: `Asked of the ${(askItem?.targetLabel ?? 'Control room').toLowerCase()}`,
    figure: String(open.length),
    unit: 'open',
    comparison: open[0]
      ? [{ text: open[0].text, strong: true }, { text: ` · ${LOOP_STATE_QUESTION}` }]
      : answered[0]?.answer
        ? [{ text: loopEvidenceAnsweredLabel(answered[0].answer), strong: true }, { text: ` · ${answered[0].text}` }]
        : [{ text: 'vs ' }, { text: '0', strong: true }, { text: ' answered' }],
    tone: open.length > 0 ? 'info' : 'neutral',
    ask: askItem ? { item: askItem, open: askItem.status === 'open' } : undefined,
  };

  return [
    {
      id: 'manual',
      name: 'Loops in manual',
      figure: String(kpis.manualCount),
      unit: `of ${kpis.total}`,
      comparison: manualComparison,
      tone: kpis.manualCount > 0 ? 'warn' : 'neutral',
      meter: { value: kpis.manualCount, max: kpis.total, tone: loopManualMeterTone(kpis.manualPct) },
    },
    longestCell,
    {
      id: 'readings',
      name: 'Readings in normal range',
      figure: String(inRange.length),
      unit: `of ${rows.length}`,
      comparison: readingsComparison,
      tone: out ? 'warn' : 'neutral',
    },
    askCell,
  ];
}

export function processTabHeadlines(
  loops: readonly ControlLoopState[],
  rows: readonly ReadingRow[],
): Record<ProcessTab, string> {
  return {
    'loop-status': `${loops.length} loops`,
    readings: `${rows.filter((row) => row.chart).length} charted`,
  };
}
